import {AuthorizerGenerator} from 'pusher-js';

import {FrontConfig} from '../../srcCommon/bundler/bundlerDefineConfig';
import {leaveBreadcrumb} from '../../srcCommon/services/bugsnag/bugsnag';
import {AblySocket} from '../../srcCommon/services/push/ably';
import {DualPusherAblySocket} from '../../srcCommon/services/push/dualPusherAbly';
import {PusherSocket} from '../../srcCommon/services/push/pusher';
import {PushSocket} from '../../srcCommon/services/push/pushSocket';
import {SocketIOSocket} from '../../srcCommon/services/push/socket.io';
import {isVisitorTeammate} from '../helpers/modelHelpers';
import {CompanyModel} from '../models/api/companyModel';
import {PushConfigModel, PushConnectorsEnum} from '../models/api/pushConfigModel';
import {TeammateModel} from '../models/api/teammateModel';
import {postAsync} from './http/http';

export function makePushSocket(
  config: PushConfigModel,
  getCompany: () => CompanyModel,
  getHasPusherAblyComparison: () => boolean,
  getHasAblyAuthority: () => boolean,
  getIsNetworkOnline: () => boolean,
  user: TeammateModel,
): PushSocket {
  if (config.connector === PushConnectorsEnum.SOCKET_IO) {
    return new SocketIOSocket(FrontConfig.API_URL);
  }

  const makeAblySocket = () => {
    const environment =
      config.connector === PushConnectorsEnum.PUSHER ? config.ably.environment : config.environment;

    return new AblySocket(
      config.roomsPrefix,
      environment,
      !isVisitorTeammate(user),
      getCompany,
      getIsNetworkOnline,
      user.id,
    );
  };

  if (config.connector === PushConnectorsEnum.ABLY) {
    return makeAblySocket();
  }

  const makePusherSocket = () => {
    const company = getCompany();

    return new PusherSocket(
      config.key,
      config.cluster,
      config.roomsPrefix,
      {
        shouldRegisterGlobalChannels: !isVisitorTeammate(user),
        shouldUpdateGlobalSocketId: true,
      },
      makePusherAuthorizer(getCompany),
      getIsNetworkOnline,
      company.id,
      user.id,
    );
  };

  if (config.connector === PushConnectorsEnum.PUSHER && !getHasPusherAblyComparison()) {
    return makePusherSocket();
  }

  const pusherSocket = makePusherSocket();
  const ablySocket = makeAblySocket();

  return new DualPusherAblySocket(pusherSocket, ablySocket, getHasPusherAblyComparison, getHasAblyAuthority);
}

function makePusherAuthorizer(getCompany: () => CompanyModel): AuthorizerGenerator {
  return (channel) => ({
    authorize: async (socketId, callback) => {
      leaveBreadcrumb('[pusher] Authorizing pusher', {
        socket_id: socketId,
        channel_name: channel.name,
      });

      try {
        const company = getCompany();
        const response = await postAsync(`${company.url}/pusher/auth`, {
          socket_id: socketId,
          channel_name: channel.name,
        });

        return callback(false, response);
      } catch (err) {
        return callback(true, err.status ? String(err.status) : JSON.stringify(err));
      }
    },
  });
}
